import { v4 as uuidv4 } from 'uuid';
import { HTTP_CLIENT } from '../../middleware/axiosClient';
import staticBrokerages from '../../brokerages/static-brokerages.json';
import {
  CASH_TRANSFER,
  DEPOSIT,
  DIVIDEND,
  FEE,
  INTEREST,
  WITHDRAW,
} from '../constants/transaction-types';

const MONEY_MARKET_DESCRIPTION_KEY = 'MONEY MARKET';

export function addCommasToNumber(value, fixedValue) {
  const num = parseFloat(value);
  if (isNaN(num)) return value;

  const minPrecision = fixedValue || 2;
  let actualPrecision = minPrecision;
  
  // If the number is very small and would round to 0 with the default precision, use more decimals
  if (Math.abs(num) > 0 && Math.abs(num) < Math.pow(10, -minPrecision)) {
    // Find how many decimal places are needed to show a non-zero value
    let temp = Math.abs(num);
    actualPrecision = minPrecision;
    while (temp < 1 && actualPrecision < 10) {
      temp *= 10;
      actualPrecision++;
    }
  }

  let rounded = num.toFixed(actualPrecision).replace(/\.?0+$/, '');

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = rounded.split('.');
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
}

export function addCommasToWholeNumber(value) {
  const stringValue = parseFloat(value).toFixed(2).toString();
  const [integerPart, decimalPart] = stringValue.split('.'); // eslint-disable-line no-unused-vars
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return formattedInteger;
}

/**
 * Shared cash total logic: filters out money-market positions and sums by type.
 * Used by holdings (total cash when no account balances) and Transactions (cash summary cards).
 * @param {Array} cashPositions - Cash position records (date, type or displayType, amount, description)
 * @returns {{ deposit: number, withdraw: number, dividend: number, cashTransfer: number, fee: number, interest: number, net: number }}
 */
export function calculateCashTotalsFromPositions(cashPositions = []) {
  const totals = {
    deposit: 0,
    withdraw: 0,
    cashTransfer: 0,
    dividend: 0,
    fee: 0,
    interest: 0,
  };

  const nonMoneyMarket = (cashPositions || []).filter((position) => {
    const description = (position.description || '').toUpperCase();
    return !description.includes(MONEY_MARKET_DESCRIPTION_KEY);
  });

  nonMoneyMarket.forEach((position) => {
    const amount = parseFloat(position.amount) || 0;
    const type = position.type || position.displayType || '';
    switch (type) {
      case DEPOSIT:
        totals.deposit += amount;
        break;
      case WITHDRAW:
        totals.withdraw += amount;
        break;
      case CASH_TRANSFER:
        totals.cashTransfer += amount;
        break;
      case DIVIDEND:
        totals.dividend += amount;
        break;
      case FEE:
        totals.fee += amount;
        break;
      case INTEREST:
        totals.interest += amount;
        break;
      default:
        break;
    }
  });

  totals.net =
    totals.deposit +
    totals.dividend +
    totals.interest +
    totals.cashTransfer -
    totals.withdraw -
    totals.fee;

  return totals;
}

export async function calculateNumberFields(
  fee,
  numberOfShares,
  purchasePrice,
  profile,
  currentPrice
) {
  let assetClass;
  let sector;

  if (profile) {
    const isCrypto = profile.exchange === 'CRYPTO';
    if (isCrypto) {
      assetClass = 'Crypto';
    } else if (profile.isEtf || profile.isFund) {
      assetClass = 'ETF / Mutual Fund';
    } else {
      assetClass = profile.sector === 'Real Estate' ? 'REIT' : 'Individual';
    }

    currentPrice = profile.price;
    sector = isCrypto ? 'Crypto' : profile.sector;
  }

  const totalPurchaseAmount = parseFloat(purchasePrice) * parseFloat(numberOfShares) + parseFloat(fee);
  const totalValueNow = parseFloat(currentPrice) * parseFloat(numberOfShares);
  const totalGainLoss = totalValueNow - totalPurchaseAmount;
  const totalGainLossPercent = totalPurchaseAmount !== 0 ? (totalGainLoss / totalPurchaseAmount) * 100 : 0;

  if (sector === '') {
    sector = 'Other';
  }

  return {
    assetClass,
    currentPrice,
    sector,
    totalPurchaseAmount,
    totalValueNow,
    totalGainLoss,
    totalGainLossPercent,
  };
}

export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Format YYYY-MM-DD and return MMM DD, YYYY
export function dashDateToFormattedDate(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  // Format the date as MMM DD, YYYY
  const formattedDate = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  });

  return formattedDate;
}

// Convert MM/DD/YYYY to MMM DD, YYYY
export function slashDateToFormattedDate(dateString) {
  const [month, day, year] = dateString.split('/').map(Number);
  const date = new Date(year, month - 1, day);

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

/**
 * Formats a number with appropriate suffix (K, M, B, T) for financial display
 * @param {number} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 1)
 * @param {boolean} includeDollarSign - Whether to include $ prefix (default: true)
 * @returns {string} Formatted number string (e.g., "$1.2M", "$500K", "$2.1B", "$100T")
 */
export function formatFinancialNumber(value, decimals = 1, includeDollarSign = true) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'N/A';
  }

  const num = Math.abs(value);
  let suffix = '';
  let divisor = 1;

  if (num >= 1000000000000) {
    suffix = 'T';
    divisor = 1000000000000;
  } else if (num >= 1000000000) {
    suffix = 'B';
    divisor = 1000000000;
  } else if (num >= 1000000) {
    suffix = 'M';
    divisor = 1000000;
  } else if (num >= 1000) {
    suffix = 'K';
    divisor = 1000;
  }

  const formattedValue = (num / divisor).toFixed(decimals);
  const cleanValue = formattedValue.replace(/\.?0+$/, ''); // Remove trailing zeros

  const prefix = includeDollarSign ? '$' : '';
  return `${prefix}${cleanValue}${suffix}`;
}

/**
 * Formats camelCase field names into readable display text
 * @param {string} fieldName - The camelCase field name
 * @returns {string} - Formatted display name
 * @example
 * formatFieldName('cashAndCashEquivalents') // Returns 'Cash And Cash Equivalents'
 * formatFieldName('netIncome') // Returns 'Net Income'
 * formatFieldName('totalAssets') // Returns 'Total Assets'
 */
export function formatFieldName(fieldName) {
  return fieldName
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
    .replace(/\s+/g, ' ') // Clean up multiple spaces
    .trim();
}

// Format YYYY-MM-DD and return a full local date
export function dashDateToFullLocalDate(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

// Format YYYY-MM-DD and return a full UTC date
export function dashDateToFullUtcDate(dateString) {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

// Format YYYY-MM-DD and return MM/DD/YYYY
export function dashDateToSlashDate(dateString) {
  const date = new Date(dateString);
  const utcMonth = String(date.getUTCMonth() + 1).padStart(2, '0');
  const utcDay = String(date.getUTCDate()).padStart(2, '0');
  const utcYear = date.getUTCFullYear();
  return `${utcMonth}/${utcDay}/${utcYear}`;
}

// Format MM/DD/YYYY and return YYYY-MM-DD
export function slashDateToDashDate(dateString) {
  if (!dateString) {
    return '';
  }

  const [month, day, year] = dateString.split('/');
  const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  return isoDate;
}

/** Parse any date string (slash or ISO) and return YYYY-MM-DD, or original string if invalid. */
export function dateStringToISODate(str) {
  if (!str) return '';
  const d = new Date(str.replace(/\//g, '-'));
  if (isNaN(d.getTime())) return str;
  return d.toISOString().split('T')[0];
}

// Get start and end dates based on a date range string
export function getDateRange(dateRange) {
  const now = new Date();
  let startDate, endDate;

  switch (dateRange) {
    case 'Last 7 days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      endDate = now;
      break;
    case 'This month so far':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = now;
      break;
    case 'Last month':
      startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case 'Last 3 months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      endDate = now;
      break;
    case 'Last 6 months':
      startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
      endDate = now;
      break;
    case 'Last 1 year':
      startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      endDate = now;
      break;
    case 'Last 5 years':
      startDate = new Date(now.getFullYear() - 5, now.getMonth(), now.getDate());
      endDate = now;
      break;
    default:
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      endDate = now;
  }

  return { startDate, endDate };
}

export function formatLargeNumber(number) {
  const roundedNumber = Math.round(number);
  if (roundedNumber >= 1_000_000_000_000) {
    return `${Math.round(roundedNumber / 1_000_000_000_000)}T`;
  } else if (roundedNumber >= 1_000_000_000) {
    return `${Math.round(roundedNumber / 1_000_000_000)}B`;
  } else if (roundedNumber >= 1_000_000) {
    return `${Math.round(roundedNumber / 1_000_000)}M`;
  } else if (roundedNumber >= 1_000) {
    return `${Math.round(roundedNumber / 1_000)}K`;
  }
  return `${roundedNumber}`;
}

export async function getCombinedTableData(individualTableData, soldTableData) {
  const strippedIndividualData = individualTableData.map((row) => ({
    date: row.purchaseDate,
    type: row.type,
    ticker: row.ticker,
    shares: parseFloat(row.numberOfShares),
    totalAmount: parseFloat(row.totalPurchaseAmount),
    id: row._id,
  }));

  const strippedSoldData = soldTableData.flatMap((row) => [
    {
      date: row.purchaseDate, // Purchase Date for old buys
      type: row.oldType || row.type, // Type for old buys/dividends/splits
      ticker: row.ticker,
      shares: parseFloat(row.sharesSold),
      totalAmount: parseFloat(row.totalPurchaseAmount),
      id: uuidv4(), // New id for old buys
    },
    {
      date: row.sellDate, // Sell Date for sells
      type: 'Sell', // Type for sells
      ticker: row.ticker,
      shares: parseFloat(row.sharesSold),
      totalAmount: parseFloat(row.totalPurchaseAmount),
      id: row._id, // Original id for sells
      oldType: row.oldType || 'Sell', // Old type
    },
  ]);

  // Combine and sort by date
  return [...strippedIndividualData, ...strippedSoldData].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
}

export function sortPositionMasterList(positionMasterList) {
  // Flatten the positionMasterList into an array of entries
  const entries = Object.entries(positionMasterList);

  // Combine all nested objects into a single array of objects
  const combinedObjects = entries.flatMap(([ticker, tickerData]) =>
    Object.entries(tickerData).map(([id, data]) => ({
      id,
      ticker,
      ...data,
    }))
  );

  // Sort the combined objects based on the date property
  combinedObjects.sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA - dateB;
  });

  return combinedObjects;
}

export function createStockValuesObject(stockData, endDate, startDate) {
  let stockValues = {};

  if (!stockData.length) {
    stockData = Object.values(stockData);
  }

  stockData.forEach(({ symbol, historical, investmentType, sector }) => {
    let lastOpenPrice = null;

    for (
      let date = new Date(startDate);
      date <= endDate;
      date.setDate(date.getDate() + 1)
    ) {
      const isoDate = date.toISOString().split('T')[0];
      if (!stockValues[isoDate]) {
        stockValues[isoDate] = {};
      }

      // If open price is available for this date, update lastOpenPrice
      const openPriceEntry = historical.find((entry) => entry.date === isoDate);
      if (openPriceEntry) {
        lastOpenPrice = openPriceEntry.open;
      } else if (lastOpenPrice === null) {
        // Find the nearest historical date with a valid open price (before or after)
        for (let i = 1; i <= historical.length; i++) {
          const previousDate = new Date(date);
          previousDate.setDate(previousDate.getDate() - i);
          const previousIsoDate = previousDate.toISOString().split('T')[0];
          const previousOpenPriceEntry = historical.find(
            (entry) => entry.date === previousIsoDate
          );
          if (previousOpenPriceEntry) {
            lastOpenPrice = previousOpenPriceEntry.open;
            break;
          }

          const nextDate = new Date(date);
          nextDate.setDate(nextDate.getDate() + i);
          const nextIsoDate = nextDate.toISOString().split('T')[0];
          const nextOpenPriceEntry = historical.find(
            (entry) => entry.date === nextIsoDate
          );
          if (nextOpenPriceEntry) {
            lastOpenPrice = nextOpenPriceEntry.open;
            break;
          }
        }
      }

      stockValues[isoDate][symbol] = {
        shares: 0,
        investmentType,
        sector,
        openPrice: lastOpenPrice || 1,
        total: 0,
      };
    }
  });

  return stockValues;
}

export async function mergeAndSumByTicker(transactions) {
  const mergedTransactions = {};

  transactions.forEach((transaction) => {
    const ticker = transaction.ticker;

    if (!mergedTransactions[ticker]) {
      // Initialize with transaction data
      mergedTransactions[ticker] = {
        ...transaction,
        purchasePrice: parseFloat(transaction.purchasePrice),
        totalGainLossPercent: parseFloat(transaction.totalGainLossPercent),
        fee: parseFloat(transaction.fee),
        numberOfShares: parseFloat(transaction.numberOfShares),
        totalPurchaseAmount: parseFloat(transaction.totalPurchaseAmount),
        totalValueNow: parseFloat(transaction.totalValueNow),
        totalGainLoss: parseFloat(transaction.totalGainLoss),
      };
    } else {
      const existing = mergedTransactions[ticker];
      const shares = parseFloat(transaction.numberOfShares);
      const purchasePrice = parseFloat(transaction.purchasePrice);

      // Update number of shares
      const totalShares = existing.numberOfShares + shares;

      // Update weighted purchase price
      existing.purchasePrice =
        (existing.purchasePrice * existing.numberOfShares +
          purchasePrice * shares) /
        totalShares;

      // Update other fields
      existing.numberOfShares = totalShares;
      existing.totalGainLossPercent =
        (existing.totalGainLossPercent * existing.totalPurchaseAmount +
          transaction.totalGainLossPercent *
            parseFloat(transaction.totalPurchaseAmount)) /
        (existing.totalPurchaseAmount +
          parseFloat(transaction.totalPurchaseAmount));
      existing.fee += parseFloat(transaction.fee);
      existing.totalPurchaseAmount += parseFloat(transaction.totalPurchaseAmount);
      existing.totalValueNow += parseFloat(transaction.totalValueNow);
      existing.totalGainLoss += parseFloat(transaction.totalGainLoss);
    }
  });

  return Object.values(mergedTransactions);
}

export async function sortValuesByDate(values, key) {
  values.sort((a, b) => {
    const dateA = a[key] ? new Date(a[key]) : new Date(0); // Parse date directly
    const dateB = b[key] ? new Date(b[key]) : new Date(0); // Parse date directly

    return dateB - dateA; // Sort by decending order
  });

  return values;
}

export function createMessageBar(message, failed) {
  const messageBar = document.createElement('message-bar-element');
  messageBar.message = message;
  messageBar.failed = failed;
  document.body.appendChild(messageBar);
}

export function setBrokerageFromUrl(index) {
  const urlParts = window.location.pathname.split('/');
  const name = decodeURIComponent(urlParts[urlParts.length - index].toLowerCase());

  const currentBrokerage = staticBrokerages.find(
    (brokerage) => brokerage.name.toLowerCase() === name
  ) || {};

  return currentBrokerage;
}

/**
 * Returns true if the profile is for a money market fund (e.g. SPAXX).
 * Used to skip storing money market as bought/sold positions; they are treated as cash via account balances.
 */
export function isMoneyMarketProfile(profile) {
  return profile?.companyName?.toLowerCase().includes('money market');
}

export async function getTickerProfiles(tickers) {
  try {
    if (!tickers || tickers.length === 0) return {};

    const response = await HTTP_CLIENT.get('/get-profiles', {
      params: { tickers: tickers.join(',') }
    });

    return response.data?.profileMap || {};
  } catch (error) {
    console.error('Error fetching ticker profiles from backend:', error);
  }
}

export async function insertNewProfileTickers(tickers) {
  try {
    if (!tickers || tickers.length === 0) return;

    await HTTP_CLIENT.post('/insert-profiles', {
      tickers: tickers
    });
  } catch (error) {
    console.error('Error inserting new ticker profiles:', error);
  }
}

export async function getTickerDividends(tickers) {
  try {
    if (!tickers || tickers.length === 0) return {};

    const response = await HTTP_CLIENT.get('/get-dividends', {
      params: { tickers: tickers.join(',') }
    });

    return response.data?.dividendMap || {};
  } catch (error) {
    console.error('Error fetching ticker dividends from backend:', error);
    return {};
  }
}

export async function insertNewDividendTickers(tickers) {
  try {
    if (!tickers || tickers.length === 0) return;

    await HTTP_CLIENT.post('/insert-dividends', {
      tickers: tickers
    });
  } catch (error) {
    console.error('Error inserting new ticker dividend histories:', error);
  }
}

export async function getTickerPrices(tickers, from, to) {
  try {
    if (!tickers || tickers.length === 0 || !from || !to) return {};

    const response = await HTTP_CLIENT.get('/get-prices', {
      params: { tickers: tickers.join(','), from, to }
    });

    return response.data?.priceMap || {};
  } catch (error) {
    console.error('Error fetching ticker prices from backend:', error);
    return {};
  }
}

export async function insertNewTickerPrices(tickers, from, to) {
  try {
    if (!tickers || tickers.length === 0) return;

    const today = new Date();
    const defaultTo = to || today.toISOString().split('T')[0];

    const pastDate = new Date();
    pastDate.setFullYear(pastDate.getFullYear() - 6);
    const defaultFrom = from || pastDate.toISOString().split('T')[0];

    for (const ticker of tickers) {
      await HTTP_CLIENT.post('/insert-prices', {
        ticker,
        from: defaultFrom,
        to: defaultTo
      });
    }
  } catch (error) {
    console.error('Error inserting new ticker prices:', error);
  }
}

export async function insertNewTickerDataIfMissing(tickers) {
  if (!tickers) return;

  const tickerArray = Array.isArray(tickers) ? tickers : [tickers];

  const validTickers = tickerArray.filter(ticker => 
    ticker && 
    typeof ticker === 'string' && 
    ticker.trim() !== '' && 
    ticker !== 'TICKER'
  );
  
  if (validTickers.length === 0) return;
  
  try {
    // Check if profiles already exist before inserting
    // (existing tickers are updated via 24-hour stale data refresh)
    const existingProfiles = await getTickerProfiles(validTickers);
    const missingProfiles = validTickers.filter(ticker => !existingProfiles[ticker]);
    
    // Only insert profiles for tickers we've never seen before
    if (missingProfiles.length > 0) {
      await insertNewProfileTickers(missingProfiles);
    }
    
    // Only insert price data for tickers we've never seen before
    if (missingProfiles.length > 0) {
      await insertNewTickerPrices(missingProfiles);
    }
    
    // Only insert dividend data for tickers we've never seen before
    if (missingProfiles.length > 0) {
      await insertNewDividendTickers(missingProfiles);
    }
  } catch (error) {
    console.error('Error inserting new ticker data:', error);
  }
}

export function calculateTradeGainLoss(boughtTableData, soldTableData) {
  let totalGainLoss = 0;
  let totalGainLossPercent = 0;
  let totalSellAmount = 0;

  if (soldTableData && soldTableData.length > 0 && boughtTableData && boughtTableData.length > 0) {
    const soldTrades = [...soldTableData].sort((a, b) => new Date(a.sellDate) - new Date(b.sellDate));
    const boughtTrades = [...boughtTableData].sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));

    let remainingBoughtShares = boughtTrades.map(trade => ({
      ...trade,
      numberOfShares: parseFloat(trade.numberOfShares || 0),
      totalPurchaseAmount: parseFloat(trade.totalPurchaseAmount || 0)
    }));
    
    for (const soldTrade of soldTrades) {
      let remainingSharesToSell = parseFloat(soldTrade.sharesSold || soldTrade.numberOfShares || 0);
      const totalSharesThisSell = remainingSharesToSell;
      let tradeGainLoss = 0;

      while (remainingSharesToSell > 0 && remainingBoughtShares.length > 0) {
        const boughtTrade = remainingBoughtShares[0];
        const boughtShares = parseFloat(boughtTrade.numberOfShares || 0);
        const sharesToUse = Math.min(remainingSharesToSell, boughtShares);

        const buyValue = (sharesToUse / boughtShares) * parseFloat(boughtTrade.totalPurchaseAmount || 0);
        const sellValue = totalSharesThisSell > 0
          ? (sharesToUse / totalSharesThisSell) * parseFloat(soldTrade.totalSellingAmount || soldTrade.totalSellAmount || 0)
          : 0;

        tradeGainLoss += sellValue - buyValue;
        remainingSharesToSell -= sharesToUse;
        
        if (sharesToUse === boughtShares) {
          remainingBoughtShares.shift();
        } else {
          remainingBoughtShares[0].numberOfShares = boughtShares - sharesToUse;
          remainingBoughtShares[0].totalPurchaseAmount = (remainingBoughtShares[0].numberOfShares / boughtShares) * parseFloat(boughtTrade.totalPurchaseAmount || 0);
        }
      }
      
      totalGainLoss += tradeGainLoss;
    }

    totalSellAmount = soldTableData.reduce((sum, row) => {
      return sum + parseFloat(row.totalSellingAmount || row.totalSellAmount || 0);
    }, 0);

    totalGainLossPercent = totalSellAmount > 0 ? (totalGainLoss / totalSellAmount) * 100 : 0;
  }

  return {
    totalGainLoss,
    totalGainLossPercent
  };
}

export function calculateUnrealizedGainLoss(boughtTableData, soldTableData, currentPositionsData) {
  let costBasisRemaining = 0;
  let currentValue = 0;

  if (!boughtTableData || boughtTableData.length === 0) {
    return {
      totalUnrealizedGainLoss: 0,
      totalUnrealizedGainLossPercent: 0,
    };
  }

  const boughtTrades = [...boughtTableData].sort((a, b) => new Date(a.purchaseDate) - new Date(b.purchaseDate));
  let remainingBoughtShares = boughtTrades.map((trade) => ({
    numberOfShares: parseFloat(trade.numberOfShares || 0),
    totalPurchaseAmount: parseFloat(trade.totalPurchaseAmount || 0),
  }));

  if (soldTableData && soldTableData.length > 0) {
    const soldTrades = [...soldTableData].sort((a, b) => new Date(a.sellDate) - new Date(b.sellDate));
    for (const soldTrade of soldTrades) {
      let remainingSharesToSell = parseFloat(soldTrade.sharesSold || soldTrade.numberOfShares || 0);
      while (remainingSharesToSell > 0 && remainingBoughtShares.length > 0) {
        const boughtTrade = remainingBoughtShares[0];
        const boughtShares = parseFloat(boughtTrade.numberOfShares || 0);
        const sharesToUse = Math.min(remainingSharesToSell, boughtShares);
        remainingSharesToSell -= sharesToUse;
        if (sharesToUse >= boughtShares) {
          remainingBoughtShares.shift();
        } else {
          remainingBoughtShares[0].numberOfShares = boughtShares - sharesToUse;
          remainingBoughtShares[0].totalPurchaseAmount =
            (remainingBoughtShares[0].numberOfShares / boughtShares) * parseFloat(boughtTrade.totalPurchaseAmount || 0);
        }
      }
    }
  }

  costBasisRemaining = remainingBoughtShares.reduce((sum, lot) => sum + parseFloat(lot.totalPurchaseAmount || 0), 0);

  if (currentPositionsData && currentPositionsData.length > 0) {
    currentValue = currentPositionsData.reduce((sum, pos) => {
      const value = parseFloat(pos.totalValueNow || 0);
      if (value > 0) return sum + value;
      const shares = parseFloat(pos.numberOfShares || 0);
      const price = parseFloat(pos.currentPrice || 0);
      return sum + shares * price;
    }, 0);
  }

  // Cannot meaningfully compute unrealized without current position data (current value is unknown)
  if (!currentPositionsData || currentPositionsData.length === 0) {
    return {
      totalUnrealizedGainLoss: 0,
      totalUnrealizedGainLossPercent: 0,
    };
  }

  const totalUnrealizedGainLoss = currentValue - costBasisRemaining;
  const totalUnrealizedGainLossPercent =
    costBasisRemaining > 0 ? (totalUnrealizedGainLoss / costBasisRemaining) * 100 : 0;

  return {
    totalUnrealizedGainLoss,
    totalUnrealizedGainLossPercent,
  };
}
